import { motion } from 'framer-motion';
import { ArrowRight, Check, X } from 'lucide-react';

import confettiImg from '../../../../assets/pages/payment/confetti.png';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../../hooks/modalContext';
import { Icon } from '../../../Icons/Icon';
import { useAuth } from '../../../../auth/Auth';
import { useSubscriptionPoints } from '../../../hooks/useSubscriptionPoints';
import { usePoints } from '../../../hooks/pointsContext';
import { useEffect, useState } from 'react';

import { formatDateToDisplayShortMonth, getDayWithSuffix } from '../../../utils/utils';
import { getBuildingDetailsById, getProcessDate } from '../../../../auth/_requests';
import { openIdentityVerificationModal } from '../../../utils/login';
import {
  DueDateTypeEnum,
  getAdvancePaymentText,
  getBusinessDays,
  getPaymentProcessingDays,
} from '../../../utils/paymentUtils';
import { usePaymentOptions } from '../../../hooks/PaymentOptionsContext';
import { PaymentMethodType } from '../../../utils/types';
import { EnumUtil } from '../../../utils/enumUtils';
import {
  IDV_AUTH_HOLD_HOURS_LABEL,
  IDV_COMPLETION_DEADLINE_HOURS_LABEL,
} from '../../../../constants/idvAuthHoldConstants';
import { mapNuveiErrorMessage } from '../../../utils/nuveiPaymentUtils';
import { ReferralPrompt } from './ReferralPrompt';

const IDV_PENDING_STEPS = [
  { label: 'Payment set up', status: 'Done' as const },
  { label: 'Rent payment made', status: 'Done' as const },
  { label: 'Verify your identity', status: '2 min' as const },
];

interface PaymentStatusProps {
  status: 'approved' | 'declined' | 'pending' | 'error';

  paymentType?: 'direct' | 'subscription';
}

const PaymentStatus: React.FC<PaymentStatusProps> = ({ status, paymentType }) => {
  const { setModal, modalState } = useModal();
  const { message } = modalState || {};
  const tenantBuildingId = modalState?.tenantBuildingId || '';
  const processDate = modalState?.processDate;
  const tenantPaymentOptionId = modalState?.tenantPaymentOptionId;
  const pendingIdvAuthHold = modalState?.pendingIdvAuthHold === true;
  const earnedPoints = Number(modalState?.earnedPoints ?? 0);
  const showReferralPrompt = modalState?.showReferralPrompt === true;
  const [convertedProcessDate, setConvertedProcessDate] = useState<string | null>(null);
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { paymentOptions } = usePaymentOptions();
  const selectedPaymentOption = paymentOptions.find(
    (option) => option.id === tenantPaymentOptionId
  );
  const paymentMethod = EnumUtil.toEnum(selectedPaymentOption?.paymentMethod, PaymentMethodType);
  const { pointsDescription, selectedDueDateType } = useSubscriptionPoints(
    auth?.data?.token,
    undefined,
    tenantBuildingId,
    selectedPaymentOption?.paymentMethodId,
    selectedPaymentOption?.cardCompanyTypeId,
    paymentMethod,
    selectedPaymentOption?.businessDaysBeforeDueDate
  );
  const { refreshPoints } = usePoints();
  const [isInitialPaymentRequired, setIsInitialPaymentRequired] = useState<boolean>(false);
  const [dueDate, setDueDate] = useState<string | number>('');
  const [dueDateType, setDueDateType] = useState<string | DueDateTypeEnum | undefined>(undefined);
  const matchedPoints =
    pointsDescription.find((p) => p.code === selectedDueDateType)?.points || '0 TP points earned';
  const businessDays = getBusinessDays(
    selectedPaymentOption?.businessDaysBeforeDueDate,
    paymentMethod
  );
  const paymentProcessingDays = getPaymentProcessingDays(
    selectedPaymentOption?.businessDaysBeforeDueDate,
    paymentMethod
  );

  useEffect(() => {
    const fetchAndSetProcessDate = async () => {
      if (processDate && dueDate && paymentType && tenantPaymentOptionId) {
        const result = await fetchProcessDate(processDate);
        setConvertedProcessDate(result);
      }
    };
    fetchAndSetProcessDate();
  }, [processDate, dueDate, paymentType, tenantPaymentOptionId]);

  useEffect(() => {
    if (tenantBuildingId) {
      fetchBuildingDetails();
    }
  }, [tenantBuildingId]);

  useEffect(() => {
    if (status !== 'approved') return;
    const timer = window.setTimeout(() => {
      refreshPoints().catch((err) => console.error('Failed to refresh points', err));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [status, refreshPoints]);

  const fetchBuildingDetails = async () => {
    try {
      const details = await getBuildingDetailsById(auth?.data?.token, tenantBuildingId);
      setIsInitialPaymentRequired(details?.data[0]?.building?.isInitialPaymentRequired);
      setDueDate(details?.data[0]?.building?.dueDate);
      setDueDateType(details?.data[0]?.dueDateType?.name);
    } catch (e) {
      console.error('Error fetching building details', e);
    }
  };

  const fetchProcessDate = async (date: string): Promise<any> => {
    try {
      if (!dueDate || !paymentType || !tenantPaymentOptionId) {
        console.error('dueDate, paymentType, or tenantPaymentOptionId is missing');
        return null;
      }
      // getProcessDate will handle UTC conversion internally
      const response = await getProcessDate(
        auth?.data?.token,
        date,
        typeof dueDate === 'number' ? dueDate : Number(dueDate),
        paymentType,
        tenantPaymentOptionId
      );
      return formatDateToDisplayShortMonth(response.data.processDate);
    } catch (error) {
      console.error('Error fetching process date', error);
      return null;
    }
  };

  return (
    <motion.div
      className='w-[554px] bg-[#FFF] rounded-[12px] flex flex-col overflow-auto relative'
      initial='hidden'
      animate='visible'
      exit='exit'
      style={{
        backgroundImage: status === 'approved' ? `url(${confettiImg})` : 'none',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top',
      }}
    >
      <div className='flex justify-end p-4'>
        <X
          className='cursor-pointer text-gray-500 hover:text-gray-700'
          size={24}
          onClick={() => setModal(null)}
        />
      </div>
      <div className='flex flex-col gap-4 px-10 pb-8'>
        <div className='flex flex-col justify-center items-center gap-1'>
          {status === 'approved' && curlyCheck}
          <span
            className={`font-bold text-[22px] ${
              status === 'approved'
                ? 'text-success'
                : status === 'declined' || status === 'error'
                  ? 'text-failure -mt-24'
                  : 'text-warning'
            }`}
          >
            {status === 'approved'
              ? pendingIdvAuthHold
                ? 'Last step'
                : paymentType === 'direct'
                  ? paymentMethod === PaymentMethodType.PreAuthorizedDebit
                    ? 'Payment under processing'
                    : 'Payment successful'
                  : 'Autopay setup successful'
              : status === 'declined'
                ? paymentType === 'direct'
                  ? 'Payment declined'
                  : 'Autopay setup declined'
                : status === 'pending'
                  ? 'Payment pending'
                  : status === 'error'
                    ? 'Payment error'
                    : ''}
          </span>
          {status === 'approved' &&
            !pendingIdvAuthHold &&
            paymentType === 'direct' &&
            convertedProcessDate && (
              <span className='text-main text-base '>
                {paymentMethod === PaymentMethodType.PreAuthorizedDebit
                  ? `Payment will be processed within ${paymentProcessingDays} business days and sent to landlord's account on ${convertedProcessDate}`
                  : `Rent will be sent to landlord's account on ${convertedProcessDate}`}
              </span>
            )}
        </div>
        {status === 'approved' && pendingIdvAuthHold && (
          <div className='flex flex-col px-6 py-8 items-start justify-center gap-5 rounded-[20px] bg-[rgba(33,81,236,0.06)]'>
            <div className='flex flex-col gap-2'>
              <span className='text-primary text-[18px] font-bold'>You're almost there</span>
              <span className='text-main text-sm'>
                Your rent payment is <span className='font-bold'>almost</span> done. Just complete a
                quick identity check, about 2 minutes, and you're finished.
              </span>
            </div>
            <ul className='flex w-full flex-col gap-3'>
              {IDV_PENDING_STEPS.map((step, index) => {
                const isDone = step.status === 'Done';
                return (
                  <li key={step.label} className='flex items-center justify-between gap-3'>
                    <div className='flex items-center gap-2.5'>
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                          isDone
                            ? 'bg-success text-white'
                            : 'border-2 border-primary bg-white text-primary'
                        }`}
                      >
                        {isDone ? <Check className='h-3.5 w-3.5' strokeWidth={3} /> : index + 1}
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          isDone ? 'text-main' : 'text-primary font-semibold'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                    <span
                      className={`text-xs font-semibold ${
                        isDone ? 'text-success' : 'text-primary'
                      }`}
                    >
                      {step.status}
                    </span>
                  </li>
                );
              })}
            </ul>
            <span className='text-main text-sm'>
              You need to complete identity verification within{' '}
              {IDV_COMPLETION_DEADLINE_HOURS_LABEL} or your rent to the landlord will be delayed.
            </span>
            <button
              className='submitBtn w-full'
              onClick={() => {
                openIdentityVerificationModal(setModal, auth?.data?.token || '', tenantBuildingId, {
                  pendingIdvAuthHold: true,
                });
              }}
            >
              <div className='flex justify-between w-full px-6'>
                <span className='font-semibold'>Verify my identity · 2 min</span>
                <ArrowRight className='text-white h-5 w-5' />
              </div>
            </button>
            <div className='flex w-full flex-col gap-2'>
              <button
                className='h-[52px] rounded-[10px] w-full px-4 py-3 flex items-center justify-center bg-[rgba(33,81,236,0.08)]'
                onClick={() => {
                  navigate('/dashboard');
                  setModal(null);
                }}
              >
                <div className='flex justify-between w-full p-4'>
                  <span className='text-primary text-base font-semibold -ml-3'>
                    Remind me in {IDV_AUTH_HOLD_HOURS_LABEL}
                  </span>
                  <ArrowRight className='text-primary h-5 w-5' />
                </div>
              </button>
            </div>
          </div>
        )}
        {status === 'approved' && !pendingIdvAuthHold && (
          <>
            {paymentType === 'direct' ? (
              <div className='flex flex-col px-6 py-8 items-start justify-center gap-5 rounded-[20px] bg-[rgba(3,155,115,0.08)]'>
                {earnedPoints > 0 && (
                  <span className='text-[18px] font-bold text-success'>
                    {earnedPoints.toLocaleString()} TP points earned
                  </span>
                )}
                <span className='text-main text-base font-semibold'>
                  Set up autopay & earn TP points/month
                </span>
                <span className='text-main text-sm'>
                  Set it and forget it! 🎯 Make rent stress-free by turning on autopay, your rent
                  will magically take care of itself every month on your chosen date. 💫🏡
                </span>
                <button
                  className='submitBtnGreen'
                  onClick={() => {
                    navigate('/pay-now', {
                      state: { buildingId: tenantBuildingId, initialStep: 1, payPlan: 'autopay' },
                    });
                    setModal(null);
                  }}
                >
                  <div className='flex justify-between w-full px-6'>
                    <span className='font-semibold'>Set up autopay</span>
                    <Icon name='refreshDouble' className='text-white h-5 w-5' />
                  </div>
                </button>
              </div>
            ) : (
              <div className='flex flex-col px-6 py-8 items-start justify-center gap-5 rounded-[20px] bg-[rgba(33,81,236,0.06)]'>
                <span className='text-success text-[18px] font-bold'>
                  🎉 Autopay is ON!
                  {Number(matchedPoints) > 0 && <> You’ll earn {matchedPoints} every month.</>}
                </span>

                <span className='text-main text-sm'>
                  Sit back and relax: with autopay, your rent is charged on your selected date, and
                  you’ll collect TenantPay Points automatically. 💫🏡
                </span>
                {isInitialPaymentRequired && (
                  <span className='font-bold'>
                    {isInitialPaymentRequired && (
                      <span className='font-bold text-main text-sm'>
                        Your autopay was set up less than {businessDays} business days before your
                        next due date, so your first rent payment will be processed on{' '}
                        <strong>{convertedProcessDate || processDate}</strong>. After that, all
                        future payments will automatically be deducted{' '}
                        <strong>
                          {getAdvancePaymentText(
                            dueDateType,
                            selectedPaymentOption?.businessDaysBeforeDueDate,
                            paymentMethod
                          )}{' '}
                          before the {dueDate}
                          <sup>{getDayWithSuffix(dueDate)}</sup>
                        </strong>{' '}
                        of each month, making rent payments effortless and on time. 💫
                      </span>
                    )}
                  </span>
                )}
                <button
                  className='h-[52px] rounded-[10px] w-full px-4 py-3 flex items-center justify-center bg-[rgba(33,81,236,0.08)]'
                  onClick={() => {
                    navigate('/rent-reporting');
                    setModal(null);
                  }}
                >
                  <div className='flex justify-between w-full p-4'>
                    <span className='text-primary text-base font-semibold'>
                      Set up credit reporting
                    </span>
                    <ArrowRight className='text-primary h-5 w-5' />
                  </div>
                </button>
              </div>
            )}
          </>
        )}

        {status === 'approved' && !pendingIdvAuthHold && showReferralPrompt && <ReferralPrompt />}

        {(status === 'error' || status === 'declined') && (
          <div className='flex flex-col px-6 py-8 items-start justify-center gap-3 rounded-[20px] bg-[#FFF1F2] border border-[#FECDD3]'>
            <span className='text-[#9F1239] text-[18px] font-bold'>Payment unsuccessful</span>
            <span className='text-[#881337] text-sm leading-relaxed'>
              {mapNuveiErrorMessage(message, 'direct-pay')}
            </span>
          </div>
        )}
        {status === 'error' || status === 'declined' ? (
          <button
            className='submitBtn mt-4'
            onClick={() => {
              navigate('/pay-now', {
                state: {
                  buildingId: tenantBuildingId,
                  initialStep: 2,
                  payPlan: paymentType === 'subscription' ? 'autopay' : 'direct',
                },
              });
              setModal(null);
            }}
          >
            <div className='flex justify-between w-full p-4'>
              <span className=' text-base font-semibold'>Try again</span>
              <ArrowRight className=' h-5 w-5' />
            </div>
          </button>
        ) : status === 'approved' && !pendingIdvAuthHold ? (
          <button
            className='submitBtn mt-10'
            onClick={() => {
              navigate('/dashboard');
              setModal(null);
            }}
          >
            <div className='flex justify-between w-full p-4'>
              <span>Home</span>
              <ArrowRight className='text-white h-5 w-5' />
            </div>
          </button>
        ) : null}
      </div>
    </motion.div>
  );
};

export { PaymentStatus };

const curlyCheck = (
  <svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M16.6111 3.53119C18.4292 1.53485 21.5704 1.53485 23.3885 3.5312L25.0888 5.39825C25.5065 5.85686 26.1068 6.10551 26.7264 6.07655L29.2489 5.95866C31.9461 5.83261 34.1673 8.05376 34.0412 10.751L33.9233 13.2735C33.8944 13.8931 34.143 14.4934 34.6016 14.911L36.4687 16.6114C38.465 18.4295 38.465 21.5706 36.4687 23.3887L34.6016 25.0891C34.143 25.5067 33.8944 26.107 33.9233 26.7266L34.0412 29.2491C34.1673 31.9463 31.9461 34.1675 29.2489 34.0414L26.7264 33.9236C26.1068 33.8946 25.5065 34.1432 25.0888 34.6019L23.3885 36.4689C21.5704 38.4653 18.4292 38.4652 16.6111 36.4689L14.9108 34.6019C14.4931 34.1432 13.8929 33.8946 13.2732 33.9236L10.7507 34.0414C8.05351 34.1675 5.83236 31.9463 5.95841 29.2491L6.0763 26.7266C6.10526 26.107 5.85661 25.5067 5.398 25.0891L3.53095 23.3887C1.5346 21.5706 1.53461 18.4295 3.53095 16.6114L5.398 14.911C5.85661 14.4934 6.10526 13.8931 6.0763 13.2735L5.95841 10.751C5.83236 8.05375 8.05352 5.83261 10.7507 5.95866L13.2732 6.07655C13.8929 6.10551 14.4931 5.85686 14.9108 5.39825L16.6111 3.53119ZM21.5401 5.21451C20.7137 4.30708 19.2859 4.30708 18.4595 5.21451L16.7592 7.08156C15.8403 8.0905 14.5197 8.63753 13.1565 8.57382L10.634 8.45593C9.40801 8.39864 8.39839 9.40825 8.45569 10.6343L8.57358 13.1568C8.63729 14.5199 8.09026 15.8406 7.08132 16.7594L5.21426 18.4597C4.30683 19.2861 4.30683 20.714 5.21426 21.5404L7.08132 23.2407C8.09026 24.1595 8.63729 25.4802 8.57358 26.8433L8.45569 29.3658C8.39839 30.5919 9.40801 31.6015 10.634 31.5442L13.1565 31.4263C14.5197 31.3626 15.8403 31.9096 16.7592 32.9185L18.4595 34.7856C19.2859 35.693 20.7137 35.693 21.5401 34.7856L23.2404 32.9185C24.1593 31.9096 25.4799 31.3626 26.8431 31.4263L29.3656 31.5442C30.5916 31.6015 31.6012 30.5919 31.5439 29.3658L31.426 26.8433C31.3623 25.4802 31.9094 24.1595 32.9183 23.2407L34.7853 21.5404C35.6928 20.714 35.6928 19.2861 34.7853 18.4597L32.9183 16.7594C31.9094 15.8406 31.3623 14.5199 31.426 13.1568L31.5439 10.6343C31.6012 9.40825 30.5916 8.39864 29.3656 8.45593L26.8431 8.57382C25.4799 8.63753 24.1593 8.0905 23.2404 7.08156L21.5401 5.21451Z'
      fill='#089B19'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M14.1159 19.1162C14.6041 18.628 15.3955 18.628 15.8837 19.1162L18.3331 21.5656L24.1159 15.7828C24.6041 15.2947 25.3955 15.2947 25.8837 15.7828C26.3718 16.271 26.3718 17.0624 25.8837 17.5506L19.217 24.2173C18.7289 24.7054 17.9374 24.7054 17.4493 24.2173L14.1159 20.8839C13.6278 20.3958 13.6278 19.6043 14.1159 19.1162Z'
      fill='#089B19'
    />
  </svg>
);
